import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { costLabel, fetchHistoryPage, type HistoryItemWire, type HistoryQueryOptions } from "../api/history";
import { useAuth } from "../auth/AuthProvider";
import { toDisplayUrl } from "../bench/mapping";
import { HistoryDrawer } from "../history/HistoryDrawer";

/** Join truthy class names. */
const cx = (...c: Array<string | false | null | undefined>): string => c.filter(Boolean).join(" ");

// Filter option lists — mirror the API's known status/operation enums; "" = no filter.
// STATUSES must match core GENERATION_STATUSES (= the api's `status` zod enum); any other value
// 400s. Mirrored locally to keep the SPA dependency-light (see enums.ts / KNOWN_METALS).
const STATUSES = ["pending", "generating", "complete", "failed", "rejected"];
const OPERATIONS = ["METAL_SWAP", "STONE_SWAP", "METAL_AND_STONE_SWAP"];

/** The non-search filters that key the query (search is applied only on submit, as `q`). */
interface Filters {
  status?: string;
  operation?: string;
  q?: string;
}

export function History() {
  const { service } = useAuth();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState("");
  const [operation, setOperation] = useState("");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [selected, setSelected] = useState<HistoryItemWire | null>(null);

  // Build the filter object with conditional spreads so exactOptionalPropertyTypes never sees an
  // explicit `undefined` (which would also make the queryKey unstable).
  const filters: Filters = {
    ...(status ? { status } : {}),
    ...(operation ? { operation } : {}),
    ...(submittedSearch ? { q: submittedSearch } : {}),
  };

  const query = useInfiniteQuery({
    queryKey: ["history", filters],
    queryFn: ({ pageParam }) =>
      fetchHistoryPage(service, { ...filters, ...(pageParam ? { cursor: pageParam } : {}) } as HistoryQueryOptions),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <main className="lab lab-hist">
      <div className="lab-head">
        <div>
          <p className="lab-eyebrow">Aardvark Jewellery — Image Lab</p>
          <h1 className="lab-title">History</h1>
          <p className="lab-sub">Every generation the pipeline has run — filter, inspect the exact request, and replay or review one.</p>
        </div>
      </div>

      <form
        className="lab-histbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmittedSearch(search);
        }}
      >
        <input className="input" placeholder="Search ring SKU or name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select className="input" value={operation} onChange={(e) => setOperation(e.target.value)} aria-label="Filter by operation">
          <option value="">All operations</option>
          {OPERATIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <button className="btn" type="submit">
          Search
        </button>
      </form>

      {query.isError && <p className="error small">history load failed: {(query.error as Error).message}</p>}
      {query.isLoading && <p className="lab-empty">Loading history…</p>}
      {!query.isLoading && !query.isError && items.length === 0 && <p className="lab-empty">No generations match these filters.</p>}

      <div className="lab-histgrid">
        {items.map((item) => {
          const url = toDisplayUrl(item.output_image_url ?? item.source_image_url);
          return (
            <button key={item.id} type="button" className="lab-histcard" onClick={() => setSelected(item)}>
              {url ? <img src={url} alt={`${item.ring_sku} ${item.operation}`} /> : <span className="noimg">no image</span>}
              <span className="cap">
                <span className="l1">
                  <span className="sku">{item.ring_sku}</span>
                  <span>· {item.operation} ·</span>
                  <span className={cx(`s-${item.status}`)}>{item.status}</span>
                </span>
                <span className="l2">
                  {costLabel(item.cost_micros)} · {item.model ?? "—"} · {new Date(item.created_at).toLocaleString("en-GB")}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {query.hasNextPage && (
        <button className="btn lab-loadmore" type="button" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {query.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}

      {selected && (
        <HistoryDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ["history"] })}
        />
      )}
    </main>
  );
}
